import React, { ReactElement, UIEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { difference } from 'lodash-es';
import cx from 'classnames';

import ArticlesTagsSearch from '../ArticlesTagsSearch';
import ArticlesTagsGroupDivider from '../ArticlesTagsGroupDivider';
import ButtonSmall from '../../../../sharable/components/ButtonSmall';
import ArticlesTagsOptionItem from '../ArticlesTagsOptionItem';

import {
  changeArticlesAppliedFilterTags,
  fetchArticlesFilterTags
} from '../../../reducers/loadArticlesSlice';
import styles from './ArticlesTagsOverlay.module.scss';
import { useDebouncedCallback } from 'use-debounce';
import {
  selectArticlesAppliedFilterTags,
  selectArticlesFilterOffset,
  selectArticlesFilterTags
} from "../../../reducers/articlesSelectors";

const LOAD_MORE_THRESHOLD = 50;
const DEBOUNCE_TIME = 200;

interface IArticlesTagsOverlayProps {
  className?: string;
  onHide?: () => void;
  isTabsV2Enabled?: boolean;
}

const ArticlesTagsOverlay = ({
  className,
  onHide,
  isTabsV2Enabled
}: IArticlesTagsOverlayProps): ReactElement => {
  const allFilterTags = useSelector(selectArticlesFilterTags);
  const appliedFilterTags = useSelector(selectArticlesAppliedFilterTags);
  const dispatch = useDispatch();
  const [selectedTags, setSelectedTags] = useState(appliedFilterTags);
  const [searchValue, setSearchValue] = useState('');

  const handleSelectTag = useCallback(
    tag => {
      const isKeySelected = selectedTags.includes(tag);
      setSelectedTags(isKeySelected ? difference(selectedTags, [tag]) : [...selectedTags, tag]);
    },
    [selectedTags]
  );

  const handleApply = useCallback(() => {
    dispatch(changeArticlesAppliedFilterTags(selectedTags));
    onHide();
  }, [dispatch, onHide, selectedTags]);

  const handleClear = useCallback(() => {
    setSelectedTags([]);
    dispatch(changeArticlesAppliedFilterTags([]));
    onHide();
  }, [dispatch, onHide]);

  const { handleSearch, handleScroll, unselectedTags } = useByVersions({
    useV2: isTabsV2Enabled,
    searchValue,
    setSearchValue,
    allFilterTags,
    selectedTags
  });

  return (
    <div className={cx(styles.container, className)}>
      <ArticlesTagsSearch onChange={handleSearch} />
      <ArticlesTagsGroupDivider />
      <div className={styles.scrollContainer} onScroll={handleScroll}>
        {allFilterTags.length ? (
          <>
            {selectedTags.map(tag => (
              <ArticlesTagsOptionItem key={tag} tag={tag} selected onSelect={handleSelectTag}>
                {tag}
              </ArticlesTagsOptionItem>
            ))}
            {selectedTags.length > 0 && <ArticlesTagsGroupDivider />}
            {unselectedTags.map(tag => (
              <ArticlesTagsOptionItem
                key={tag}
                tag={tag}
                selected={false}
                onSelect={handleSelectTag}
              >
                {tag}
              </ArticlesTagsOptionItem>
            ))}
          </>
        ) : (
          <span className={styles.noArticlesTagsFound}>No articles tags found.</span>
        )}
      </div>
      <div className={styles.buttonsContainer}>
        <ButtonSmall className={styles.clearButton} onClick={handleClear}>
          Clear Filters
        </ButtonSmall>
        <ButtonSmall className={styles.applyButton} onClick={handleApply}>
          Apply
        </ButtonSmall>
      </div>
    </div>
  );
};

interface UseByVersionsProps {
  useV2: boolean;
  searchValue: string;
  setSearchValue: (value: string) => void;
  allFilterTags: string[];
  selectedTags: string[];
}

// Ugly hook to handle v1 and v2 versions of the functionality, will be removed after v2 stable
function useByVersions(props: UseByVersionsProps) {
  const { useV2, searchValue, setSearchValue, allFilterTags, selectedTags } = props;

  const offset = useSelector(selectArticlesFilterOffset);

  const dispatch = useDispatch();

  const unselectedTags = useMemo(() => {
    const filteredTags = allFilterTags.filter(tag =>
      tag.toLowerCase().includes(searchValue.toLowerCase())
    );

    const sourceFilterTags = useV2 ? allFilterTags : filteredTags;

    return difference(sourceFilterTags, selectedTags);
  }, [allFilterTags, searchValue, selectedTags, useV2]);

  const handleScrollDebounced = useDebouncedCallback((el: HTMLDivElement) => {
    const hasMore = offset !== -1;
    if (hasMore) {
      const { scrollHeight, scrollTop, clientHeight } = el;

      if (scrollHeight - scrollTop - clientHeight < LOAD_MORE_THRESHOLD) {
        dispatch(fetchArticlesFilterTags({ search: searchValue, offset, useV2: true }));
      }
    }
  }, DEBOUNCE_TIME);

  const handleSearchDebounced = useDebouncedCallback((value: string) => {
    dispatch(fetchArticlesFilterTags({ search: value, offset: 0, useV2: true }));
  }, DEBOUNCE_TIME);

  const handleSearch = useCallback(
    value => {
      setSearchValue(value);
      if (useV2) {
        handleSearchDebounced(value);
      }
    },
    [handleSearchDebounced, setSearchValue, useV2]
  );

  useEffect(() => {
    return () => {
      if (useV2) {
        dispatch(fetchArticlesFilterTags({ search: '', offset: 0, useV2: true }));
      }
    };
  }, [dispatch, useV2]);

  return {
    handleSearch,
    handleScroll: useV2
      ? (event: UIEvent<HTMLDivElement>) => handleScrollDebounced(event.target as HTMLDivElement)
      : undefined,
    unselectedTags
  };
}

export default ArticlesTagsOverlay;
